import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArchiveIcon from "@mui/icons-material/Archive";
import { ReactNode, useState, MouseEvent, Children, isValidElement } from "react";
import { useDelete, useRecordContext, useResourceContext, useNotify } from "react-admin";
import { useNavigate } from "react-router-dom";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { ColorType, ConfirmLabelType } from "@/types/shared/uiComponents";
import { AlertType } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface CommonActionConfig {
  /** Enable standard delete action */
  delete?:
    | boolean
    | {
        redirect?: string;
        successMessage?: string;
        confirmTitle?: string;
        confirmContent?: string;
      };
  /** Enable standard edit action */
  edit?: boolean;
  /** Enable standard duplicate/clone action */
  duplicate?: boolean | { onDuplicate?: () => void };
  /** Enable standard archive action */
  archive?: boolean | { onArchive?: () => void };
}

/** A page-specific entry for the overflow (kebab) menu: label + one icon + handler. */
export interface PageHeaderOverflowItem {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface PageHeaderActionsProps {
  /** Primary, page-specific affordances — always rendered inline (visible). */
  children?: ReactNode;
  /** Standard record actions (delete/edit/duplicate/archive) — placed in the overflow (kebab) menu. */
  actions?: CommonActionConfig;
  /**
   * Page-specific low-frequency actions for the same kebab, listed before the
   * standard record actions. One menu per header: a page must not render its own.
   */
  extraItems?: PageHeaderOverflowItem[];
}

interface OverflowItem extends PageHeaderOverflowItem {
  danger?: boolean;
}

/**
 * Action container for a record page header.
 *
 * Priority-based, not count-based: custom **children are primary affordances and
 * always render inline** (comments, tasks, upload, …). The standard record actions
 * passed via `actions` (delete/edit/duplicate/archive) — typically destructive or
 * low-frequency — live in an overflow **kebab** menu. The menu is built from typed
 * descriptors (label + single icon), so it can't mis-render arbitrary children
 * (the previous "doubled icon / 'Action'" bug).
 */
export const PageHeaderActions = ({ children, actions = {}, extraItems = [] }: PageHeaderActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);
  const record = useRecordContext();
  const resource = useResourceContext();
  const navigate = useNavigate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const theme = useTheme();
  // On mobile, stack the inline children vertically instead of letting full-width
  // buttons wrap mid-row. (Children stay inline rather than collapsing into the
  // kebab — rendering arbitrary children in a menu is what caused the prior
  // doubled-icon bug; the kebab is reserved for typed record-action descriptors.)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const deleteConfig = typeof actions.delete === "object" ? actions.delete : {};
  const defaultRedirect = `/${resource}`;

  const handleDelete = () => {
    if (!record?.id || !resource) return;
    void deleteOne(
      resource,
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          setDeleteConfirmOpen(false);
          notify(deleteConfig.successMessage || `${resource} deleted successfully`, { type: AlertType.SUCCESS });
          void navigate(deleteConfig.redirect || defaultRedirect);
        },
        onError: (error) => {
          notifyError(error);
        },
      }
    );
  };

  // Build overflow (kebab) descriptors: page-specific entries first, then the standard record actions.
  const overflowItems: OverflowItem[] = [...extraItems];
  if (actions.edit && record?.id && resource) {
    overflowItems.push({
      key: "edit",
      label: "Edit",
      icon: <EditIcon fontSize="small" />,
      onClick: () => navigate(`/${resource}/${record.id}`),
    });
  }
  if (actions.duplicate && record?.id) {
    const cfg = typeof actions.duplicate === "object" ? actions.duplicate : {};
    overflowItems.push({
      key: "duplicate",
      label: "Duplicate",
      icon: <ContentCopyIcon fontSize="small" />,
      onClick: cfg.onDuplicate || (() => console.log("Duplicate action not implemented")),
    });
  }
  if (actions.archive && record?.id) {
    const cfg = typeof actions.archive === "object" ? actions.archive : {};
    overflowItems.push({
      key: "archive",
      label: "Archive",
      icon: <ArchiveIcon fontSize="small" />,
      onClick: cfg.onArchive || (() => console.log("Archive action not implemented")),
    });
  }
  if (actions.delete && record?.id && resource) {
    overflowItems.push({
      key: "delete",
      label: "Delete Record",
      icon: <DeleteIcon fontSize="small" />,
      onClick: () => setDeleteConfirmOpen(true),
      disabled: isDeleting,
      danger: true,
    });
  }

  const customChildren = children ? Children.toArray(children).filter(isValidElement) : [];

  if (customChildren.length === 0 && overflowItems.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          flexWrap: isMobile ? "nowrap" : "wrap",
          justifyContent: "flex-end",
        }}
      >
        {customChildren}

        {overflowItems.length > 0 && (
          <>
            <Tooltip title="More actions">
              <IconButton
                onClick={handleClick}
                size="small"
                aria-label="More actions"
                sx={{
                  border: 1,
                  borderColor: "divider",
                  alignSelf: isMobile ? "flex-end" : "auto",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <MoreHorizIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {overflowItems.map((item) => (
                <MenuItem
                  key={item.key}
                  disabled={item.disabled}
                  onClick={() => {
                    item.onClick();
                    handleClose();
                  }}
                  sx={{ minWidth: 180, ...(item.danger ? { color: "error.main" } : {}) }}
                >
                  <ListItemIcon sx={item.danger ? { color: "error.main" } : undefined}>{item.icon}</ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>

      <ConfirmationDialog
        open={deleteConfirmOpen}
        title={deleteConfig.confirmTitle || `Delete ${record?.aegle_id || record?.id}`}
        message={deleteConfig.confirmContent || "You will not be able to recover this record. Are you sure?"}
        confirmLabel={ConfirmLabelType.Delete}
        confirmColor={ColorType.Error}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};
